import { useMemo } from 'react'
import PropTypes from 'prop-types'

import { breakTextToLines } from '../../helpers/textHelpers'
import { adjustColorBrightness, shouldUseScrollableContent } from './helpers'

import { darkGrey } from '../../../../../../../../styles/const/colors'
import useStyles from './styles'

// Pill node component for flow diagram with hover support
function Pill({
  x,
  y,
  w,
  h,
  r = 8,
  fill = '#eee',
  text = '',
  secondaryText = '',
  textColor,
  onMouseEnter,
  onMouseLeave,
  onMouseMove,
  isHovered = false,
  isExporting = false
}) {
  const cx = x + w / 2
  const textY = y + h + 10 // Position text 10px below the pill
  const fillText = textColor || '#111'

  const classes = useStyles({
    fillText
  })

  // Apply hover effect to fill color
  const pillFill = isHovered ? adjustColorBrightness(fill, 20) : fill

  // Break text into lines (approximate 25 chars for 187px pill width)
  const textLines = useMemo(() => breakTextToLines(text, 25), [text])
  const secondaryLines = useMemo(() => breakTextToLines(secondaryText, 25), [secondaryText])

  // Check if content is too long and requires scrollable rendering
  // Disable scrolling during export to show all content in PNG
  const useScrollableContent = useMemo(
    () => !isExporting && shouldUseScrollableContent(textLines, secondaryLines),
    [textLines, secondaryLines, isExporting]
  )

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        rx={r}
        ry={r}
        fill={pillFill}
        style={{ cursor: onMouseEnter ? 'pointer' : 'default' }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onMouseMove={onMouseMove}
      />
      {useScrollableContent ? (
        <foreignObject x={cx - w / 2} y={textY} width={w} height={60}>
          <div className={classes.pill}>
            <div>{text}</div>
            {secondaryText && <div style={{ color: darkGrey, marginTop: '4px' }}>{secondaryText}</div>}
          </div>
        </foreignObject>
      ) : (
        <text
          x={cx}
          y={textY}
          textAnchor="middle"
          dominantBaseline="hanging"
          fontSize={12}
          fontWeight={400}
          fontStyle="normal"
          style={{ pointerEvents: 'none' }} // Prevent text from interfering with hover
        >
          {textLines.map((line, i) => (
            <tspan key={`text-${i}`} x={cx} dy={i === 0 ? 0 : 14} fill={fillText}>
              {line}
            </tspan>
          ))}
          {secondaryLines.map((line, i) => (
            <tspan key={`secondary-${i}`} x={cx} dy={14} fill={darkGrey}>
              {line}
            </tspan>
          ))}
        </text>
      )}
    </g>
  )
}

Pill.propTypes = {
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  w: PropTypes.number.isRequired,
  h: PropTypes.number.isRequired,
  r: PropTypes.number,
  fill: PropTypes.string,
  text: PropTypes.string,
  secondaryText: PropTypes.string,
  textColor: PropTypes.string,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onMouseMove: PropTypes.func,
  isHovered: PropTypes.bool,
  isExporting: PropTypes.bool
}

export default Pill
